'use client';
import { useTranslations as useTranslations$1, useLocale } from 'next-intl';
/**
 * AI Film Budget — the interactive page. Inputs on the left, in four sections;
 * a summary panel on the right that follows the scroll and owns the exports.
 * The math lives in model.ts; the Excel lives in xlsx.ts; this file is the UI.
 *
 * State lives in the URL (replaceState, non-defaults only) so "Copy link"
 * hands someone exactly this budget.
 */ import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ArrowRight, Check, ChevronDown, Copy, Download, HelpCircle, Link as LinkIcon } from '@/components/ui/icons';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { OliveIcon } from '@/components/icons/OliveIcon';
import { VideoBackground } from '@/components/VideoBackground';
import { Link } from '@/i18n/navigation';
import { cn } from '@/lib/utils';
import { captureSentryError } from '@/lib/sentry';
import { trackFilmBudgetExported, trackFilmBudgetInputChanged, trackFilmBudgetSectionViewed, trackFilmBudgetViewed, type FilmBudgetExportKind, type FilmBudgetInputSource, type FilmBudgetSection } from '@/lib/analytics';
import type { ShotBudgetRates } from '@/lib/marketing-config';
import { Control } from './Control';
import { FigureTip } from './FigureTip';
import { BUCKET, DayFigure, RuntimeFigure, ScheduleFigure, ShotsFigure, TakeFigure, TakesGrid, type Bucket } from './figures';
import { compute, DEFAULTS, fmtCents, fmtInt, fmtMin, fmtSec, fmtUsd, fmtUsdShort, MODEL_VERSION, defaultPrepDays, BUDGET_URL_KEYS, clipLenFor, MAX_CLIP_LEN, parseParams, range, resLabel, serializeParams, type BudgetParams, type NumericParam, type Resolution } from './model';
import { downloadBudgetXlsx } from './xlsx';
const BUCKETS: Bucket[] = [
    'gen',
    'art',
    'cln'
];
/** Quiet time before the budget is written to the address bar; at most 40 writes per 10 s, under WebKit's 100. */ const URL_WRITE_DELAY_MS = 250;
const SECTION_BUCKET: Partial<Record<FilmBudgetSection, Bucket>> = {
    generation: 'gen',
    personnel: 'art',
    cleanup: 'cln'
};
const BUCKET_SECTION: Record<Bucket, FilmBudgetSection> = {
    gen: 'generation',
    art: 'personnel',
    cln: 'cleanup'
};
export interface FilmBudgetProps {
    rates: ShotBudgetRates;
    /** Absolute, for the copied summary. */ pricingUrl: string;
}
export function FilmBudget({ rates, pricingUrl }: FilmBudgetProps) {
    const t = useTranslations$1();
    const locale = useLocale();
    // i18n-ignore — numeric marks, USD amounts, min/s/h units and resolution codes remain fixed.
    const RUNTIME_PRESETS: {
        minutes: number;
        label: string;
    }[] = [
        {
            minutes: 1,
            label: t("Fpk12m")
        },
        {
            minutes: 10,
            label: t("nQLog5")
        },
        {
            minutes: 45,
            label: t("HQ_L35")
        },
        {
            minutes: 90,
            label: t("UYrUJT")
        }
    ];
    /** What the total leaves out. Shown as tiles after Cleanup so it cannot be missed. */ const NOT_INCLUDED: [string, string][] = [
        [
            t("gWps1I"),
            t("LS-tN8")
        ],
        [
            t("WyyBiN"),
            t("rDla1a")
        ],
        [
            t("XZA37K"),
            t("lBuHZZ")
        ],
        [
            t("79lLl-"),
            t("A-eI8D")
        ],
        [
            t("pBQ5bd"),
            t("t_7T_k")
        ],
        [
            t("qXTiEd"),
            t("QA1sJ_")
        ],
        [
            t("NYOwWQ"),
            t("BIe-_5")
        ],
        [
            t("u6Nb1g"),
            t("QWlQVz")
        ],
        [
            t("gV4x4c"),
            t("nHIcVc")
        ]
    ];
    const bucketLabels = {
        gen: t("XfQPV0"),
        art: t("xFAUFt"),
        cln: t("XslFtA")
    };
    const [params, setParams] = useState<BudgetParams>(DEFAULTS);
    const [active, setActive] = useState<FilmBudgetSection | null>(null);
    const [done, setDone] = useState<FilmBudgetExportKind | null>(null);
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState(false);
    const [copyError, setCopyError] = useState<'link' | 'summary' | null>(null);
    const [delta, setDelta] = useState<{
        value: number;
        key: number;
    } | null>(null);
    const lastTotal = useRef<number | null>(null);
    const viewedSections = useRef(new Set<FilmBudgetSection>());
    const mounted = useRef(false);
    // The live budget query and hash, so the language links keep them even when opened in a new tab.
    const [budgetQuery, setBudgetQuery] = useState('');
    const m = useMemo(()=>compute(params, rates), [
        params,
        rates
    ]);
    const { lo, hi } = useMemo(()=>range(params, rates), [
        params,
        rates
    ]);
    // Read the URL once, after hydration (the page is static), then mirror every change back.
    useEffect(()=>{
        // Only the calculator's own keys make a link "shared"; utm_* and friends are attribution, not a budget.
        const keys = Array.from(new URLSearchParams(window.location.search).keys()).filter((k)=>BUDGET_URL_KEYS.has(k));
        // eslint-disable-next-line react-hooks/set-state-in-effect -- deep link: the page is static, so the URL (an external store) is read once after hydration.
        setParams(parseParams(window.location.search));
        setBudgetQuery(`${window.location.search}${window.location.hash}`);
        // Effects run child-first: on a direct load this runs before PostHogProvider has called initPostHog(),
        // so capture after a macrotask, as Flux3ReportTracker does. Otherwise shared-link visits are dropped.
        const t = setTimeout(()=>trackFilmBudgetViewed({
                sharedLink: keys.length > 0,
                paramKeys: keys
            }), 0);
        return ()=>clearTimeout(t);
    }, []);
    // A slider drag changes params on every pointer move. WebKit (every iOS browser, desktop Safari)
    // throws a SecurityError past 100 replaceState calls in 10 seconds, and the throw takes the whole
    // page down, so the address bar is written once the input settles instead of on every change.
    const pendingUrl = useRef<string | null>(null);
    const flushUrl = useCallback(()=>{
        if (pendingUrl.current === null) return;
        window.history.replaceState(null, '', pendingUrl.current);
        pendingUrl.current = null;
    }, []);
    useEffect(()=>{
        if (!mounted.current) {
            mounted.current = true;
            return;
        }
        // Rewrite only our keys; whatever else is on the URL (utm_*…) stays for the attribution tools that read it.
        const next = new URLSearchParams(window.location.search);
        for (const key of BUDGET_URL_KEYS)next.delete(key);
        for (const [key, value] of new URLSearchParams(serializeParams(params)))next.set(key, value);
        const qs = next.toString();
        pendingUrl.current = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
        setBudgetQuery(`${qs ? `?${qs}` : ''}${window.location.hash}`);
        const t = setTimeout(flushUrl, URL_WRITE_DELAY_MS);
        return ()=>clearTimeout(t);
    }, [
        params,
        flushUrl
    ]);
    /** The URL that reopens this budget: the address bar plus the model version, so a saved default budget is still a v1 budget. */ const shareUrl = ()=>{
        flushUrl();
        const url = new URL(window.location.href);
        url.searchParams.set('v', String(MODEL_VERSION));
        return url.toString();
    };
    // Delta chip: show the jump when a change moves the total by $500 or more.
    useEffect(()=>{
        if (lastTotal.current !== null && Math.abs(m.total - lastTotal.current) >= 500) {
            setDelta({
                value: m.total - lastTotal.current,
                key: Date.now()
            });
        }
        lastTotal.current = m.total;
    }, [
        m.total
    ]);
    useEffect(()=>{
        if (!delta) return;
        const t = setTimeout(()=>setDelta(null), 1400);
        return ()=>clearTimeout(t);
    }, [
        delta
    ]);
    // Scroll spy: the summary highlights the bucket you are editing, and each
    // section counts as viewed once.
    useEffect(()=>{
        const io = new IntersectionObserver((entries)=>{
            for (const e of entries){
                if (!e.isIntersecting) continue;
                const section = e.target.id as FilmBudgetSection;
                setActive(section);
                if (!viewedSections.current.has(section)) {
                    viewedSections.current.add(section);
                    trackFilmBudgetSectionViewed({
                        section
                    });
                }
            }
        }, {
            rootMargin: '-40% 0px -45% 0px'
        });
        document.querySelectorAll<HTMLElement>('[data-budget-section]').forEach((el)=>io.observe(el));
        // The desktop summary is a sticky aside, so it is "in view" almost always; count it once as viewed
        // but never let it take over the active bucket the way the scrolled sections do.
        const asideIo = new IntersectionObserver((entries)=>{
            if (!entries.some((e)=>e.isIntersecting) || viewedSections.current.has('summary')) return;
            viewedSections.current.add('summary');
            trackFilmBudgetSectionViewed({
                section: 'summary'
            });
        }, {
            threshold: 0.5
        });
        document.querySelectorAll<HTMLElement>('[data-budget-section-aside]').forEach((el)=>asideIo.observe(el));
        return ()=>{
            io.disconnect();
            asideIo.disconnect();
        };
    }, []);
    const set = useCallback((param: NumericParam)=>(value: number)=>{
            setParams((p)=>{
                // A typed generation length obeys the delivery route's limit too (4K: 15 s), not just the slider.
                if (param === 'clipLen') value = clipLenFor(p.res, value);
                // ...and seconds used can't exceed the generation it comes from (the model already caps it; the URL must agree)
                if (param === 'shotLen') value = Math.min(value, p.clipLen);
                return {
                    ...p,
                    [param]: value,
                    // seconds used can't exceed the generation; shrink it with the generation length
                    ...(param === 'clipLen' && p.shotLen > value ? {
                        shotLen: value
                    } : {}),
                    // prep follows the runtime until someone sets it by hand
                    ...(param === 'runtime' && p.prepDays === defaultPrepDays(p.runtime) ? {
                        prepDays: defaultPrepDays(value)
                    } : {})
                };
            });
        }, []);
    const commit = useCallback((param: string)=>(from: number | string | boolean, to: number | string | boolean, source: FilmBudgetInputSource, // Handlers that track before their setParams pass the params they are about to set, so the event carries the resulting total.
        next?: BudgetParams)=>{
            trackFilmBudgetInputChanged({
                param,
                from,
                to,
                source,
                totalUsd: next ? compute(next, rates).total : m.total
            });
        }, [
        m.total,
        rates
    ]);
    const setRes = (res: Resolution)=>{
        if (res === params.res) return;
        // 4K runs on Seedance 2.0, which stops at 15 s: pull the generation length (and the seconds used from it) down with it.
        const clipLen = clipLenFor(res, params.clipLen);
        const next = {
            ...params,
            res,
            clipLen,
            shotLen: Math.min(params.shotLen, clipLen)
        };
        commit('res')(params.res, res, 'segment', next);
        setParams(next);
    };
    const setHdr = (hdr: boolean)=>{
        const next = {
            ...params,
            hdr
        };
        commit('hdr')(params.hdr, hdr, 'toggle', next);
        setParams(next);
    };
    const resetAll = ()=>{
        commit('all')('custom', 'defaults', 'reset', DEFAULTS);
        setParams(DEFAULTS);
    };
    const flash = (kind: FilmBudgetExportKind)=>{
        setDone(kind);
        setTimeout(()=>setDone((d)=>(d === kind ? null : d)), 1800);
        trackFilmBudgetExported({
            kind,
            totalUsd: m.total,
            aiMinutes: m.aiMin
        });
    };
    /** The plain-text summary behind "Copy summary". */ function topSheetText(p: BudgetParams, rates: ShotBudgetRates, pricingUrl: string, budgetUrl: string): string {
        const m = compute(p, rates);
        const { lo, hi } = range(p, rates);
        const lines = [
            t("Wkcak7"),
            t("OI8ziq", {
                value1: String(fmtMin(m.aiMin)),
                value2: String(p.runtime),
                value3: String(fmtInt(m.keepers)),
                value4: String(resLabel(p.res))
            }),
            t("ogYWrK", {
                value1: String(fmtUsd(m.gen)),
                value2: String(fmtInt(m.takes)),
                value3: String(p.tries),
                value4: String(p.clipLen),
                value5: String(p.draftShare)
            }),
            t("McpZvP", {
                value1: String(fmtUsd(m.art)),
                value2: String(fmtInt(m.artistDays)),
                value3: String(p.prepDays),
                value4: String(fmtUsd(p.artistRate)),
                value5: String(p.team),
                value6: String(m.months.toFixed(1))
            }),
            t("1oGCZ8", {
                value1: String(fmtUsd(m.cln)),
                value2: String(fmtInt(m.flagged)),
                value3: String(fmtUsd(m.perCleanup))
            }),
            t("u30KQM", {
                value1: String(fmtUsd(m.total)),
                value2: String(m.aiMin > 0 ? t("jWUn6p", {
                    amount: fmtUsdShort(m.total / m.aiMin)
                }) : t("NkoJqx")),
                value3: String(fmtInt(m.gen / rates.oliveUsd)),
                value4: String(fmtUsdShort(lo)),
                value5: String(fmtUsdShort(hi))
            }),
            t("4sc8EZ", {
                value1: String(pricingUrl)
            }),
            budgetUrl
        ];
        return lines.join('\n');
    }
    const copy = async (kind: 'link' | 'summary')=>{
        const text = kind === 'link' ? shareUrl() : topSheetText(params, rates, pricingUrl, shareUrl());
        setCopyError(null);
        try {
            await navigator.clipboard.writeText(text);
        } catch (error) {
            setCopyError(kind);
            captureSentryError(error, {
                operation: 'film_budget_copy'
            });
            return;
        }
        flash(kind);
    };
    const exportXlsx = async ()=>{
        setExporting(true);
        setExportError(false);
        try {
            await downloadBudgetXlsx({
                params,
                rates,
                reopenUrl: shareUrl(),
                locale,
                exportedOn: new Date().toLocaleDateString(locale === 'es' ? 'es-419' : 'en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                })
            });
            flash('xlsx');
        } catch (error) {
            setExportError(true);
            captureSentryError(error, {
                operation: 'film_budget_xlsx_export'
            });
        } finally{
            setExporting(false);
        }
    };
    const usd: Record<Bucket, number> = {
        gen: m.gen,
        art: m.art,
        cln: m.cln
    };
    const takeTip = (i: number, kind: 'draft' | 'final' | 'shot')=>kind === 'shot' ? t("fqYFJM", {
            value1: String(i + 1),
            value2: String(params.tries),
            value3: String(resLabel(params.res)),
            value4: String(fmtCents(m.finalEach)),
            value5: String(m.used)
        }) : kind === 'final' ? t("6OMq44", {
            value1: String(i + 1),
            value2: String(params.tries),
            value3: String(resLabel(params.res)),
            value4: String(params.clipLen),
            value5: String(fmtCents(m.finalEach))
        }) : t("pM4_pW", {
            value1: String(i + 1),
            value2: String(params.tries),
            value3: String(params.clipLen),
            value4: String(fmtCents(m.draftEach))
        });
    const plainTakeTip = (i: number)=>t("0E2m9o", {
            value1: String(i + 1),
            value2: String(params.tries),
            value3: String(params.clipLen)
        });
    const fmtDay = (n: number)=>String(Math.round(n * 10) / 10);
    const activeBucket = active ? SECTION_BUCKET[active] : undefined;
    const K = fmtInt(m.keepers);
    const share = (b: Bucket)=>(m.total ? (usd[b] / m.total) * 100 : 0);
    const summary = (<SummaryPanel total={m.total} lo={lo} hi={hi} delta={delta} usd={usd} share={share} activeBucket={activeBucket} aiMin={m.aiMin} olives={m.gen / rates.oliveUsd} takes={m.takes} ratio={(params.tries * params.clipLen) / m.used} oliveUsd={rates.oliveUsd} done={done} exporting={exporting} exportError={exportError} copyError={copyError} onXlsx={exportXlsx} onCopy={copy} grid={// The same boxes as the hero, so every slider is seen to move them.
    <FigureTip>
          <TakesGrid tries={params.tries} clipLen={params.clipLen} used={m.used} drafts={m.drafts} colored dense={false} label={t("L6lOcW")} tipFor={takeTip}/>
          <GridLegend drafts={m.drafts} finals={m.finals} res={params.res} used={m.used} compact/>
          <p className="mt-3 text-[13px] leading-relaxed text-white/55">
            {t.rich("OsyWPo", {
        value1: String(fmtUsd(m.perKeeper)),
        value2: String(K),
        text1: (chunks)=><N>{chunks}</N>,
        text2: (chunks)=><N>{chunks}</N>
    })}
          </p>
        </FigureTip>} verdict={<>
          {t.rich("p9TZoV", {
        value1: String(fmtUsdShort(m.total)),
        value2: String(fmtMin(m.aiMin)),
        value3: String(Math.round(share('gen'))),
        text1: (chunks)=><N>{chunks}</N>,
        text2: (chunks)=><N>{chunks}</N>,
        text3: (chunks)=><N>{chunks}</N>
    })}
        </>}/>);
    return (<div className="relative">
      { /* The site's blurred reel behind the header, as on /tools; no credit badge, the blur hides the film. */ }
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[560px] overflow-hidden">
        <VideoBackground hideCredit/>
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-b from-transparent to-[#141414]"/>
      </div>

      <div className="relative mx-auto w-full max-w-6xl px-4 pt-32 sm:px-6 md:pt-36 lg:px-8">
        { /* ── Header ─────────────────────────────────────────── */ }
        <header className="max-w-3xl">
          <nav aria-label={t("pHHygR")} className="mb-5 inline-flex rounded-lg border border-white/15 p-0.5 text-xs">
            {([
        'en',
        'es'
    ] as const).map((language)=>(<a key={language} // From Spanish, English needs its explicit /en prefix: the bare URL would follow the es cookie back here, while
        // /en redirects to the bare URL and resets the cookie, in this tab or a new one.
        href={`${language === 'es' ? '/es' : locale === 'es' ? '/en' : ''}/production-calculator${budgetQuery}`} hrefLang={language} lang={language} aria-current={locale === language ? 'page' : undefined} className={cn('rounded-md px-3 py-1.5 transition-colors', locale === language ? 'bg-white/10 text-white' : 'text-white/50 hover:bg-white/[0.05] hover:text-white')}>
                { /* i18n-ignore — fixed language codes for this two-language switcher. */ }
                {language === 'en' ? 'EN' : 'ES'}
              </a>))}
          </nav>
          <p className="mb-4 text-xs font-medium uppercase tracking-[0.14em] text-white/50">
            { /* Only this page is published in Spanish, so every link on it targets the English pages. */ }
            <Link href="/tools" locale="en" className="transition-colors hover:text-white">
              {t("nUT0Lv")}
            </Link>
            <span className="mx-1.5 text-white/25">/</span>
            {t("5zW9Ng")}
            <span className="mx-1.5 text-white/25">·</span>v{MODEL_VERSION}
          </p>
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-[56px] md:leading-[1.05]">
            {t("CGYEyq")}
          </h1>
          <p className="mt-5 max-w-[46ch] text-xl leading-snug text-white/80">
            {t("r9eBI2")}
          </p>
          <div className="mt-7 max-w-[58ch] space-y-3 border-s-2 border-white/15 ps-4 text-[15px] leading-relaxed text-white/60">
            <p>
              {t("JR20l9")}
            </p>
            <p>
              {t("lZPmg4")}
            </p>
          </div>
          <p className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-white/50">
            <Link locale="en" href="/pricing#per-model-pricing" className="underline decoration-white/25 underline-offset-4 hover:text-white">
              {t("t58pgL")}
            </Link>
            <span aria-hidden className="text-white/20">
              ·
            </span>
            <a href="mailto:koh@c47.studio?subject=AI%20Film%20Budget%20feedback" className="underline decoration-white/25 underline-offset-4 hover:text-white">
              {t("G5YSJR")}
            </a>
            <span aria-hidden className="text-white/20">
              ·
            </span>
            <button type="button" onClick={resetAll} className="underline decoration-white/25 underline-offset-4 hover:text-white">
              {t("ViEXjK")}
            </button>
          </p>
        </header>

        <div className="mt-12 grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-10 xl:gap-14">
          { /* ── Left: inputs ─────────────────────────────────── */ }
          <div className="min-w-0">
            { /* ── The project ───────────────────────────────── */ }
            <Section id="project" title={t("gR3UUc")}>
              <Control param="runtime" title={t("66kGA0")} tone={BUCKET.gen.tone} value={params.runtime} defaultValue={DEFAULTS.runtime} min={1} max={180} step={1} unit={'min'} marks={[
        {
            at: 1,
            label: '1'
        },
        {
            at: 30,
            label: '30'
        },
        {
            at: 90,
            label: t("RwPbjG")
        },
        {
            at: 180,
            label: '180'
        }
    ]} onChange={set('runtime')} onCommit={commit('runtime')}>
                <div className="mt-1 flex flex-wrap gap-2">
                  {RUNTIME_PRESETS.map((preset)=>(<button key={preset.minutes} type="button" aria-pressed={params.runtime === preset.minutes} onClick={()=>{
            if (params.runtime === preset.minutes) return;
            const next = {
                ...params,
                runtime: preset.minutes,
                ...(params.prepDays === defaultPrepDays(params.runtime) ? {
                    prepDays: defaultPrepDays(preset.minutes)
                } : {})
            };
            commit('runtime')(params.runtime, preset.minutes, 'preset', next);
            setParams(next);
        }} className={cn('h-9 rounded-lg border px-3.5 text-sm transition-colors', params.runtime === preset.minutes ? 'border-white/40 bg-white/10 text-white' : 'border-white/10 bg-white/[0.03] text-white/65 hover:border-white/25 hover:text-white')}>
                      {preset.label}
                    </button>))}
                </div>
              </Control>

              <Control param="ai" title={t("3eQUN9")} hint={t("HUl8pW", {
        value1: String(fmtMin(m.aiMin)),
        value2: String(params.runtime)
    })} tone={BUCKET.gen.tone} value={params.ai} defaultValue={DEFAULTS.ai} min={0} max={100} step={5} unit="%" marks={[
        {
            at: 0,
            label: t("sUugGS")
        },
        {
            at: 50,
            label: t("msFM3h")
        },
        {
            at: 100,
            label: t("J9gGzZ")
        }
    ]} onChange={set('ai')} onCommit={commit('ai')}>
                <Figure>
                  <RuntimeFigure runtime={params.runtime} aiMin={m.aiMin} aiPct={params.ai}/>
                  <Caption>
                    {t.rich("CPKN1h", {
        value1: String(fmtMin(m.aiMin)),
        value2: String(fmtInt(m.finSec)),
        value3: String(m.used),
        value4: String(K),
        text1: (chunks)=><N>{chunks}</N>,
        text2: (chunks)=><N>{chunks}</N>,
        text3: (chunks)=><N>{chunks}</N>,
        text4: (chunks)=><N>{chunks}</N>
    })}
                  </Caption>
                </Figure>
              </Control>

              <div className="mt-8 border-t border-white/10 pt-8">
                <div className="text-base font-medium text-white">{t("8ZyDhP")}</div>
                <p className="mt-1 text-sm text-white/60">
                  {t.rich("b4TRR_", {
        text1: (chunks)=>(<Link locale="en" href="/pricing#per-model-pricing" className="underline decoration-white/25 underline-offset-4 hover:text-white">
                          {chunks}
                        </Link>)
    })}
                </p>
                <Tabs value={params.res} onValueChange={(v)=>setRes(v as Resolution)} className="mt-4">
                  <TabsList className="h-auto w-full max-w-md gap-1 rounded-xl bg-white/[0.06] p-1 sm:w-auto">
                    {([
        '720',
        '1080',
        '4k'
    ] as Resolution[]).map((res)=>(<TabsTrigger key={res} value={res} className="h-auto flex-1 flex-col gap-0.5 rounded-lg px-5 py-2 text-sm text-white/70 data-[state=active]:bg-white data-[state=active]:text-[#141414] data-[state=active]:shadow-sm sm:min-w-[7rem]">
                        <span className="font-semibold">{resLabel(res)}</span>
                        <span className="font-mono text-[11px] font-normal tabular-nums opacity-70">
                          {fmtCents(rates.perSecondUsd[res])} / s
                        </span>
                      </TabsTrigger>))}
                  </TabsList>
                </Tabs>
              </div>
            </Section>

            { /* ── Generation ─────────────────────────────────── */ }
            <Section id="generation" bucket="gen" title={t("XfQPV0")}>
              <Control param="clipLen" title={t("fZtu6K")} hint={params.res === '4k' ? t("bJAL_2") : t("HFO0aR")} tone={BUCKET.gen.tone} value={params.clipLen} defaultValue={DEFAULTS.clipLen} min={4} max={MAX_CLIP_LEN[params.res]} step={1} unit="s" marks={[
        {
            at: 4,
            label: '4 s'
        },
        {
            at: 15,
            label: '15 s'
        },
        ...(MAX_CLIP_LEN[params.res] > 15 ? [
            {
                at: 30,
                label: '30 s'
            }
        ] : [])
    ]} onChange={set('clipLen')} onCommit={commit('clipLen')}>
                <Figure>
                  <TakeFigure clipLen={params.clipLen} used={0} label={`${params.clipLen} s`} tip={t("6hVKnD", {
        value1: String(params.clipLen),
        value2: String(fmtCents(m.draftEach)),
        value3: String(fmtCents(m.finalEach)),
        value4: String(resLabel(params.res))
    })}/>
                  <Caption>
                    {t.rich("JykCJx", {
        value1: String(fmtCents(m.draftEach)),
        value2: String(fmtCents(m.finalEach)),
        value3: String(resLabel(params.res)),
        text1: (chunks)=><N>{chunks}</N>,
        text2: (chunks)=><N>{chunks}</N>
    })}
                  </Caption>
                </Figure>
              </Control>

              <Control param="shotLen" title={t("HQKRiH")} hint={t("fe0dkU")} tone={BUCKET.gen.tone} value={params.shotLen} defaultValue={DEFAULTS.shotLen} min={1} max={params.clipLen} step={0.5} unit="s" marks={[
        {
            at: 1,
            label: '1 s'
        },
        ...(params.clipLen > 6 ? [
            {
                at: 4,
                label: '4 s'
            }
        ] : []),
        ...(params.clipLen >= 12 ? [
            {
                at: 8,
                label: '8 s'
            }
        ] : []),
        {
            at: params.clipLen,
            label: t("S3mrVz", {
                value1: String(params.clipLen)
            })
        }
    ]} onChange={set('shotLen')} onCommit={commit('shotLen')}>
                <Figure>
                  <TakeFigure clipLen={params.clipLen} used={m.used} label={t("0p0_Oz", {
        value1: String(m.used),
        value2: String(Math.round((params.clipLen - m.used) * 10) / 10)
    })} tip={t("4gke0R", {
        value1: String(Math.round((params.clipLen - m.used) * 10) / 10)
    })} usedTip={t("D6N5aq", {
        value1: String(m.used)
    })}/>
                </Figure>
              </Control>

              <Control param="tries" title={t("5aj3Ay")} hint={t("hQ4D7R")} tone={BUCKET.gen.tone} value={params.tries} defaultValue={DEFAULTS.tries} min={1} max={500} step={1} unit="×" marks={[
        {
            at: 3,
            label: t("eGZYWr")
        },
        {
            at: 10,
            label: '10'
        },
        {
            at: 20,
            label: '20'
        },
        {
            at: 30,
            label: '30'
        },
        {
            at: 90,
            label: '90'
        }
    ]} onChange={set('tries')} onCommit={commit('tries')}>
                <Figure>
                  <TakesGrid tries={params.tries} clipLen={params.clipLen} used={m.used} drafts={m.drafts} colored={false} label={t("HS8_V3")} tipFor={plainTakeTip}/>
                  <Caption>
                    {t.rich("JlEsQl", {
        value1: String(params.tries),
        value2: String(params.clipLen),
        value3: String(fmtInt(params.tries * params.clipLen)),
        value4: String(m.used),
        value5: String(((params.tries * params.clipLen) / m.used).toFixed(0)),
        text1: (chunks)=><N>{chunks}</N>,
        text2: (chunks)=><N>{chunks}</N>,
        text3: (chunks)=><N>{chunks}</N>,
        text4: (chunks)=><N>{chunks}</N>,
        text5: (chunks)=><N>{chunks}</N>
    })}
                  </Caption>
                </Figure>
              </Control>

              <Control param="draftShare" title={t("oUmnHR")} hint={t("8BiAT3")} tone={BUCKET.gen.tone} value={params.draftShare} defaultValue={DEFAULTS.draftShare} min={0} max={100} step={5} unit={t("RrFfQ_")} marks={[
        {
            at: 0,
            label: t("ycxRMh")
        },
        {
            at: 80,
            label: '80%'
        },
        {
            at: 100,
            label: t("VqHOZO")
        }
    ]} onChange={set('draftShare')} onCommit={commit('draftShare')}>
                <Figure>
                  <TakesGrid tries={params.tries} clipLen={params.clipLen} used={m.used} drafts={m.drafts} colored label={t("WpbgOQ")} tipFor={takeTip}/>
                  <Caption>
                    <Swatch tone="rgba(92,216,137,0.22)"/>
                    <N>{m.drafts}</N> × {fmtCents(m.draftEach)} = <N>{fmtUsd(m.drafts * m.draftEach)}</N>
                    <span className="mx-2"/>
                    <Swatch tone="rgba(92,216,137,0.08)" outline/>
                    <N>{m.finals}</N> × {fmtCents(m.finalEach)} = <N>{fmtUsd(m.finals * m.finalEach)}</N>
                    <span className="mx-2"/>
                    <Swatch tone={BUCKET.gen.tone}/>
                    {t("0Vj6y-")}
                  </Caption>
                  <Caption>
                    {t.rich("m1C6LS", {
        value1: String(fmtUsd(m.perKeeper)),
        value2: String(fmtUsd(m.allFinal)),
        value3: String(Math.round((1 - m.perKeeper / m.allFinal) * 100)),
        text1: (chunks)=><N>{chunks}</N>,
        text2: (chunks)=><N>{chunks}</N>,
        text3: (chunks)=><N>{chunks}</N>
    })}
                  </Caption>
                </Figure>
              </Control>

              <Collapsible className="mt-8 border-t border-white/10 pt-8">
                <CollapsibleTrigger className="group inline-flex min-h-9 flex-wrap items-center gap-x-2 gap-y-0.5 rounded-lg border border-white/10 bg-white/[0.03] py-1.5 pe-3.5 ps-3 text-start text-sm text-white/70 transition-colors hover:border-white/25 hover:text-white">
                  <ChevronDown className="h-4 w-4 -rotate-90 transition-transform group-data-[state=open]:rotate-0"/>
                  {t("WifiR5")}
                  <span className="font-mono text-xs tabular-nums text-white/45">
                    {t("-3fF5j", {
        value1: String(params.imgs),
        value2: String(params.hdr ? t("t9qEPu") : t("C0MnAi"))
    })}
                  </span>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-8">
                  <Control param="imgs" title={t("kNWp4Y")} hint={t("qxc2Pt", {
        value1: String(fmtCents(rates.stillUsd))
    })} tone={BUCKET.gen.tone} value={params.imgs} defaultValue={DEFAULTS.imgs} min={0} max={150} step={5} marks={[
        {
            at: 0,
            label: '0'
        },
        {
            at: 40,
            label: '40'
        },
        {
            at: 100,
            label: '100'
        },
        {
            at: 150,
            label: '150'
        }
    ]} onChange={set('imgs')} onCommit={commit('imgs')}/>
                  <div className="mb-6 flex items-center justify-between gap-4">
                    <div>
                      <label htmlFor="hdr" className="text-base font-medium text-white">
                        {t("a8QJ9e")}
                      </label>
                      <div className="mt-1 text-sm text-white/60">
                        {t("4TGps0", {
        value1: String(fmtCents(rates.hdrPerSecondUsd[params.res])),
        value2: String(resLabel(params.res))
    })}
                      </div>
                    </div>
                    <Switch id="hdr" checked={params.hdr} onCheckedChange={setHdr}/>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </Section>

            { /* ── The people ─────────────────────────────────── */ }
            <Section id="personnel" bucket="art" title={t("xFAUFt")}>
              <Control param="gensPerDay" title={t("XGYFir")} hint={t("4JSjaq")} tone={BUCKET.art.tone} value={params.gensPerDay} defaultValue={DEFAULTS.gensPerDay} min={25} max={500} step={5} unit={t("5GX1_c")} marks={[
        {
            at: 25,
            label: '25'
        },
        {
            at: 150,
            label: t("DKlDVX")
        },
        {
            at: 300,
            label: '300'
        },
        {
            at: 500,
            label: '500'
        }
    ]} onChange={set('gensPerDay')} onCommit={commit('gensPerDay')}>
                <Figure>
                  <DayFigure shotsPerDay={m.shotsPerDay} max={Math.min(30, Math.max(15, Math.ceil(m.shotsPerDay)))}/>
                  <Caption>
                    {t.rich("uIXdIc", {
        value1: String(params.gensPerDay),
        value2: String(params.tries),
        value3: String(fmtDay(m.shotsPerDay)),
        value4: String(fmtSec(m.shotsPerDay * m.used)),
        text1: (chunks)=><N>{chunks}</N>,
        text2: (chunks)=><N>{chunks}</N>,
        text3: (chunks)=><N>{chunks}</N>,
        text4: (chunks)=><N>{chunks}</N>
    })}
                  </Caption>
                  <Caption>
                    {t.rich("hM9INF", {
        value1: String(K),
        value2: String(fmtDay(m.shotsPerDay)),
        value3: String(fmtInt(m.shotDays)),
        text1: (chunks)=><N>{chunks}</N>,
        text2: (chunks)=><N>{chunks}</N>,
        text3: (chunks)=><N>{chunks}</N>
    })}
                  </Caption>
                </Figure>
              </Control>
              <Control param="artistRate" title={t("DHT1x8")} hint={t("KpY6h3")} tone={BUCKET.art.tone} value={params.artistRate} defaultValue={DEFAULTS.artistRate} min={0} max={10000} step={50} prefix="$" marks={[
        {
            at: 100,
            label: '$100'
        },
        {
            at: 900,
            label: '$900'
        },
        {
            at: 2500,
            label: '$2,500'
        },
        {
            at: 5000,
            label: '$5,000'
        },
        {
            at: 10000,
            label: '$10,000'
        }
    ]} onChange={set('artistRate')} onCommit={commit('artistRate')}/>
              <Control param="prepDays" title={t("OvRmK4")} hint={t("iD_zU1")} tone={BUCKET.art.tone} value={params.prepDays} defaultValue={defaultPrepDays(params.runtime)} min={0} max={120} step={1} unit={t("Bc20la")} marks={[
        {
            at: 0,
            label: '0'
        },
        {
            at: 10,
            label: '10'
        },
        {
            at: 30,
            label: '30'
        },
        {
            at: 60,
            label: '60'
        },
        {
            at: 120,
            label: '120'
        }
    ]} onChange={set('prepDays')} onCommit={commit('prepDays')}/>
              <Control param="team" title={t("qgAU8u")} hint={t("yshMRX")} tone={BUCKET.art.tone} value={params.team} defaultValue={DEFAULTS.team} min={1} max={12} step={1} marks={[
        {
            at: 1,
            label: '1'
        },
        {
            at: 3,
            label: '3'
        },
        {
            at: 7,
            label: '7'
        },
        {
            at: 12,
            label: '12'
        }
    ]} onChange={set('team')} onCommit={commit('team')}>
                <Figure>
                  <ScheduleFigure months={m.months} team={params.team}/>
                  <Caption>
                    {t.rich("RuglAY", {
        value1: String(fmtInt(m.shotDays)),
        value2: String(params.prepDays),
        value3: String(fmtInt(m.artistDays)),
        value4: String(params.team),
        value5: String(m.months.toFixed(1)),
        text1: (chunks)=><N>{chunks}</N>,
        text2: (chunks)=><N>{chunks}</N>,
        text3: (chunks)=><N>{chunks}</N>,
        text4: (chunks)=><N>{chunks}</N>,
        text5: (chunks)=><N>{chunks}</N>
    })}
                  </Caption>
                </Figure>
              </Control>
            </Section>

            { /* ── Cleanup ────────────────────────────────────── */ }
            <Section id="cleanup" bucket="cln" title={t("XslFtA")} lead={t("WoeTIV")}>
              <Control param="cleanShare" title={t("4SbrCQ")} hint={t("a54uJ7")} tone={BUCKET.cln.tone} value={params.cleanShare} defaultValue={DEFAULTS.cleanShare} min={0} max={100} step={5} unit="%" marks={[
        {
            at: 0,
            label: t("_22ubR")
        },
        {
            at: 10,
            label: '10%'
        },
        {
            at: 40,
            label: '40%'
        },
        {
            at: 100,
            label: t("QxsUO1")
        }
    ]} onChange={set('cleanShare')} onCommit={commit('cleanShare')}>
                <Figure>
                  <ShotsFigure total={m.keepers} pct={params.cleanShare} cleanupTip={t("eDEgsW", {
        value1: String(params.cleanHours),
        value2: String(fmtUsd(params.cleanRate)),
        value3: String(fmtUsd(m.perCleanup))
    })}/>
                  <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-white/55">
                    <span>
                      <Swatch tone="rgba(92,216,137,0.55)"/>
                      {m.keepers > 100 ? t("XCWRAL", {
        value1: String(fmtInt(m.keepers / 100))
    }) : t("uvTu3P")}
                    </span>
                    <span>
                      <Swatch tone="rgba(92,216,137,0.55)" hatch={BUCKET.cln.tone}/>
                      {t("s74hS2")}
                    </span>
                  </p>
                  <Caption>
                    {t.rich("urgYMc", {
        value1: String(fmtInt(m.flagged)),
        value2: String(K),
        text1: (chunks)=><N>{chunks}</N>,
        text2: (chunks)=><N>{chunks}</N>
    })}
                  </Caption>
                </Figure>
              </Control>
              <Control param="cleanHours" title={t("6q5TNG")} hint={t("Q16y2O")} tone={BUCKET.cln.tone} value={params.cleanHours} defaultValue={DEFAULTS.cleanHours} min={0.5} max={24} step={0.5} unit="h" marks={[
        {
            at: 0.5,
            label: '0.5 h'
        },
        {
            at: 3,
            label: t("kPhSWQ")
        },
        {
            at: 8,
            label: t("HliZ5c")
        },
        {
            at: 16,
            label: t("ncsPUS")
        },
        {
            at: 24,
            label: '24 h'
        }
    ]} onChange={set('cleanHours')} onCommit={commit('cleanHours')}/>
              <Control param="cleanRate" title={t("P90BTp")} tone={BUCKET.cln.tone} value={params.cleanRate} defaultValue={DEFAULTS.cleanRate} min={0} max={10000} step={50} prefix="$" marks={[
        {
            at: 100,
            label: '$100'
        },
        {
            at: 250,
            label: t("zpJ02Y")
        },
        {
            at: 400,
            label: t("Fx4eVx")
        },
        {
            at: 1200,
            label: t("uWc7xR")
        },
        {
            at: 2500,
            label: '$2,500'
        }
    ]} onChange={set('cleanRate')} onCommit={commit('cleanRate')}>
                <Caption>
                  {t.rich("5xzwBn", {
        value1: String(params.cleanHours),
        value2: String(fmtUsd(params.cleanRate / 8)),
        value3: String(fmtUsd(m.perCleanup)),
        value4: String(fmtInt(m.flagged)),
        value5: String(fmtUsdShort(m.cln)),
        text1: (chunks)=><N>{chunks}</N>,
        text2: (chunks)=><N>{chunks}</N>,
        text3: (chunks)=><N>{chunks}</N>,
        text4: (chunks)=><N>{chunks}</N>,
        text5: (chunks)=><N>{chunks}</N>
    })}
                </Caption>
              </Control>
            </Section>

            { /* ── What this budget leaves out: loud on purpose, so nobody takes the total for a whole film ── */ }
            <section aria-labelledby="not-included" className="mt-6 rounded-2xl border border-dashed border-white/20 bg-transparent p-5 sm:p-7">
              <h2 id="not-included" className="text-2xl font-semibold tracking-tight">
                {t("MmqfOG")}
              </h2>
              <p className="mt-2 max-w-[62ch] text-[15px] leading-relaxed text-white/60">
                {t("YPWKfF")}
              </p>
              <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
                {NOT_INCLUDED.map(([title, note])=>(<li key={title} className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                    <div className="text-[15px] font-medium text-white">{title}</div>
                    <div className="mt-0.5 text-[13px] text-white/45">{note}</div>
                  </li>))}
              </ul>
            </section>

            { /* On narrow screens the summary follows the inputs. */ }
            <div id="summary" data-budget-section className="mt-8 scroll-mt-28 lg:hidden">
              {summary}
            </div>

            <p className="mt-10 max-w-[72ch] text-[13px] leading-relaxed text-white/40">
              {t.rich("9uaV-S", {
        value1: String(fmtCents(rates.oliveUsd)),
        text1: (chunks)=>(<Link locale="en" href="/pricing#per-model-pricing" className="underline decoration-white/20 underline-offset-4 hover:text-white/70">
                      {chunks}
                    </Link>)
    })}
            </p>
          </div>

          { /* ── Right: summary, follows the scroll ───────────── */ }
          <aside className="hidden lg:sticky lg:top-24 lg:block lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto" data-budget-section-aside>
            {summary}
          </aside>
        </div>
      </div>

      { /* ── Mobile: total + Excel, always in reach ─────────────── */ }
      <div className="sticky bottom-0 z-30 mt-10 border-t border-white/10 bg-[#141414]/90 px-4 py-3 backdrop-blur-md lg:hidden">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4">
          <a href="#summary" className="min-w-0">
            <div className="font-mono text-xl font-semibold tabular-nums leading-none text-white">
              {fmtUsdShort(m.total)}
            </div>
            <div className="mt-1 text-[11px] text-white/50">
              {t("2ms0i1", {
        value1: String(fmtUsdShort(lo)),
        value2: String(fmtUsdShort(hi))
    })}
            </div>
          </a>
          <FigureTip className="flex-1">
            <div className="flex h-2 overflow-hidden rounded-full bg-white/10" aria-hidden>
              {BUCKETS.map((b)=>(<div key={b} className="min-w-[2px]" style={{
            width: `${share(b)}%`,
            background: BUCKET[b].tone
        }} data-tip={t("4BVs3J", {
            value1: String(bucketLabels[b]),
            value2: String(fmtUsd(usd[b])),
            value3: String(Math.round(share(b)))
        })}/>))}
            </div>
          </FigureTip>
          <Button size="sm" onClick={exportXlsx} disabled={exporting} className="shrink-0 gap-1.5">
            <Download className="h-3.5 w-3.5"/>
            {t("vwxVnf")}
          </Button>
        </div>
      </div>
    </div>);
}
// ── Summary panel ─────────────────────────────────────────────────────
function SummaryPanel({ total, lo, hi, delta, usd, share, activeBucket, aiMin, olives, takes, ratio, oliveUsd, done, exporting, exportError, copyError, onXlsx, onCopy, grid, verdict }: {
    total: number;
    lo: number;
    hi: number;
    delta: {
        value: number;
        key: number;
    } | null;
    usd: Record<Bucket, number>;
    share: (b: Bucket) => number;
    activeBucket?: Bucket;
    aiMin: number;
    olives: number;
    takes: number;
    ratio: number;
    oliveUsd: number;
    done: FilmBudgetExportKind | null;
    exporting: boolean;
    exportError: boolean;
    copyError: 'link' | 'summary' | null;
    onXlsx: () => void;
    onCopy: (kind: 'link' | 'summary') => void;
    grid: ReactNode;
    verdict: ReactNode;
}) {
    const t = useTranslations$1();
    const bucketLabels = {
        gen: t("XfQPV0"),
        art: t("xFAUFt"),
        cln: t("XslFtA")
    };
    return (<div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] shadow-[0_24px_80px_-32px_rgba(0,0,0,0.8)]">
      <div className="p-6 sm:p-7">
        <div className="text-xs font-medium uppercase tracking-[0.14em] text-white/50">{t("bOkoGn")}</div>
        <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <div className="font-mono text-5xl font-semibold tabular-nums leading-none tracking-tight text-white">
            {fmtUsdShort(total)}
          </div>
          {delta && (<span key={delta.key} className={cn('rounded-full px-2 py-0.5 font-mono text-xs font-semibold tabular-nums motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200', delta.value > 0 ? 'bg-[#ff9a5c]/15 text-[#ffb98a]' : 'bg-[#5cd889]/15 text-[#8fe8ad]')}>
              {delta.value > 0 ? '+' : '−'}
              {fmtUsdShort(Math.abs(delta.value))}
            </span>)}
        </div>
        <div className="mt-2 text-[15px] text-white/55">
          {t("7HW2G0")} <span className="tabular-nums text-white/80">{fmtUsdShort(lo)}</span> –{' '}
          <span className="tabular-nums text-white/80">{fmtUsdShort(hi)}</span>
        </div>
        <p className="mt-3 text-[15px] leading-relaxed text-white/65">{verdict}</p>
        <p className="mt-2 text-xs leading-relaxed text-white/40">
          {t("7H6Sts")}
        </p>

        <FigureTip>
          <div className="mt-5 flex h-3 overflow-hidden rounded-full bg-white/10" aria-hidden>
            {BUCKETS.map((b)=>(<div key={b} className={cn('min-w-[2px] motion-safe:transition-[width,opacity] motion-safe:duration-300', activeBucket && activeBucket !== b && 'opacity-40')} style={{
            width: `${share(b)}%`,
            background: BUCKET[b].tone
        }} data-tip={t("4BVs3J", {
            value1: String(bucketLabels[b]),
            value2: String(fmtUsd(usd[b])),
            value3: String(Math.round(share(b)))
        })}/>))}
          </div>
        </FigureTip>

        <dl className="mt-4 space-y-1">
          {BUCKETS.map((b)=>(<a key={b} href={`#${BUCKET_SECTION[b]}`} className={cn('flex items-center gap-3 rounded-lg px-2 py-2 -mx-2 transition-colors hover:bg-white/[0.05]', activeBucket && activeBucket !== b && 'opacity-55')}>
              <Swatch tone={BUCKET[b].tone} size={10}/>
              <dt className="flex flex-1 items-center gap-1.5 text-[15px] text-white/85">
                {bucketLabels[b]}
                {b === 'gen' && (<Help text={t("shIIe2")}/>)}
              </dt>
              <dd className="w-10 text-right text-sm tabular-nums text-white/45">{Math.round(share(b))}%</dd>
              <dd className="w-20 text-right text-[15px] font-semibold tabular-nums text-white">{fmtUsd(usd[b])}</dd>
            </a>))}
        </dl>

        <div className={cn('mt-5 border-t border-white/10 pt-5', activeBucket && activeBucket !== 'gen' && 'opacity-55')}>
          <div className="mb-3 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-white/45">
            {t("_4dFMk")}
            <Help text={t("OceJPa")}/>
          </div>
          {grid}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-px border-y border-white/10 bg-white/10">
        <Stat label={t("8oV2me")} value={aiMin > 0 ? fmtUsdShort(total / aiMin) : '—'}/>
        <Stat label={t("8LHtTc", {
        value1: String(fmtCents(oliveUsd))
    })} value={<span className="inline-flex items-center gap-1.5">
              <OliveIcon className="h-3.5 w-3.5 text-white/50"/>
              {fmtInt(olives)}
            </span>}/>
        <Stat label={t("zfR2Za")} value={fmtInt(takes)}/>
        <Stat label={t("AiiAAs")} value={`${ratio.toFixed(0)} : 1`}/>
      </div>

      <div className="space-y-2.5 p-6 sm:p-7">
        <Button onClick={onXlsx} disabled={exporting} className="h-11 w-full gap-2 text-[15px]">
          {done === 'xlsx' ? <Check className="h-4 w-4"/> : <Download className="h-4 w-4"/>}
          {exporting ? t("ZDPrrO") : done === 'xlsx' ? t("OlwlCi") : t("XSSSwB")}
        </Button>
        <div className="grid grid-cols-2 gap-2.5">
          <Button variant="outline" onClick={()=>onCopy('link')} className="h-10 gap-2 border-white/15 bg-transparent hover:bg-white/[0.06]">
            {done === 'link' ? <Check className="h-4 w-4"/> : <LinkIcon className="h-4 w-4"/>}
            {done === 'link' ? t("p556q3") : t("lbr3Lq")}
          </Button>
          <Button variant="outline" onClick={()=>onCopy('summary')} className="h-10 gap-2 border-white/15 bg-transparent hover:bg-white/[0.06]">
            {done === 'summary' ? <Check className="h-4 w-4"/> : <Copy className="h-4 w-4"/>}
            {done === 'summary' ? t("p556q3") : t("PDFu-o")}
          </Button>
        </div>
        {exportError && (<p className="text-xs text-[#ffb98a]">{t("piLuBm")}</p>)}
        {copyError === 'link' && (<p className="text-xs text-[#ffb98a]">{t("BUcoNM")}</p>)}
        {copyError === 'summary' && (<p className="text-xs text-[#ffb98a]">{t("U0w1KR")}</p>)}
        <p className="pt-1 text-xs leading-relaxed text-white/40">
          {t("MI-Tpy")}
        </p>
        <Link locale="en" href="/pricing" className="group/link inline-flex items-center gap-1.5 pt-1 text-sm font-medium text-white/55 transition-colors hover:text-white">
          {t("YIAC8c")}
          <ArrowRight strokeWidth={1.5} className="h-3.5 w-3.5 transition-transform duration-200 group-hover/link:translate-x-0.5"/>
        </Link>
      </div>
    </div>);
}
/** A small (?) that explains itself on hover or tap. Safe inside links: the click never follows the anchor. */ function Help({ text }: {
    text: string;
}) {
    const t = useTranslations$1();
    return (<TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" aria-label={t("QqsO7W")} onClick={(e)=>e.preventDefault()} className="rounded-full text-white/40 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40">
            <HelpCircle className="h-3.5 w-3.5"/>
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px] normal-case tracking-normal">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>);
}
function Stat({ label, value }: {
    label: string;
    value: ReactNode;
}) {
    return (<div className="bg-[#171717] px-6 py-4 sm:px-7">
      <div className="text-[11px] uppercase tracking-[0.12em] text-white/45">{label}</div>
      <div className="mt-1.5 text-lg font-semibold tabular-nums text-white">{value}</div>
    </div>);
}
// ── Small pieces ─────────────────────────────────────────────────────
function Section({ id, bucket, title, lead, children }: {
    id: FilmBudgetSection;
    bucket?: Bucket;
    title: string;
    lead?: string;
    children: ReactNode;
}) {
    return (<section id={id} data-budget-section className="mt-6 scroll-mt-28 rounded-2xl border border-white/10 bg-white/[0.02] p-5 sm:p-7">
      <h2 className="flex items-center gap-2.5 text-2xl font-semibold tracking-tight">
        {bucket && <Swatch tone={BUCKET[bucket].tone} size={12}/>}
        {title}
      </h2>
      {lead && <p className="mt-2 max-w-[62ch] text-[15px] leading-relaxed text-white/60">{lead}</p>}
      <div className="mt-8">{children}</div>
    </section>);
}
const Figure = ({ children }: {
    children: ReactNode;
})=>(<FigureTip className="mt-5 rounded-xl bg-white/[0.03] px-4 pb-4 pt-4">{children}</FigureTip>);
/** What the boxes in a takes grid mean. */ function GridLegend({ drafts, finals, res, used, compact }: {
    drafts: number;
    finals: number;
    res: Resolution;
    used: number;
    compact?: boolean;
}) {
    const t = useTranslations$1();
    return (<p className={cn('mt-3 flex flex-wrap gap-x-4 gap-y-1 text-white/55', compact ? 'text-xs' : 'text-[13px]')}>
      <span>
        <Swatch tone="rgba(92,216,137,0.22)"/>
        {t("Pu9nAr", {
        value1: String(drafts)
    })}
      </span>
      <span>
        <Swatch tone="rgba(92,216,137,0.08)" outline/>
        {t("0L1zcD", {
        value1: String(finals),
        value2: String(resLabel(res))
    })}
      </span>
      <span>
        <Swatch tone={BUCKET.gen.tone}/>
        {t("lkZ_Pe", {
        value1: String(used)
    })}
      </span>
    </p>);
}
const Caption = ({ children }: {
    children: ReactNode;
})=>(<p className="mt-3 text-[15px] leading-relaxed text-white/65">{children}</p>);
/** A number in running text: the prose face, semibold, white, tabular. */ const N = ({ children }: {
    children: ReactNode;
})=><b className="font-semibold tabular-nums text-white">{children}</b>;
function Swatch({ tone, size = 10, outline, hatch }: {
    tone: string;
    size?: number;
    outline?: boolean;
    /** Diagonal stripes in this color over the tone (the cleanup marking). */ hatch?: string;
}) {
    return (<span aria-hidden className="me-1.5 inline-block shrink-0 rounded-[3px] align-[-1px]" style={{
        width: size,
        height: size,
        background: hatch ? `repeating-linear-gradient(45deg, ${hatch} 0 2px, ${tone} 2px 5px)` : tone,
        boxShadow: outline ? 'inset 0 0 0 1.5px rgba(92,216,137,0.8)' : hatch ? `inset 0 0 0 1.5px ${hatch}` : undefined
    }}/>);
}
